function sortiert(clip) {
    // `?? []`: ein Schnitt, der vor diesem Feld gespeichert wurde, hat es nicht.
    // Das Schema trägt es zwar beim Einlesen nach, aber diese Funktionen werden
    // auch auf rohe Daten losgelassen -- und eine Rechnung, die daran stirbt,
    // nimmt die ganze Zeitleiste mit.
    return [...(clip.gainKeyframes ?? [])].sort((a, b) => a.at - b.at);
}
/** Der Pegel an der Stelle `local` (Sekunden in den Clip hinein). */
export function gainAt(clip, local) {
    const punkte = sortiert(clip);
    if (punkte.length === 0)
        return clip.gain;
    if (punkte.length === 1)
        return punkte[0].gain;
    if (local <= punkte[0].at)
        return punkte[0].gain;
    const letzter = punkte[punkte.length - 1];
    if (local >= letzter.at)
        return letzter.gain;
    for (let i = 0; i < punkte.length - 1; i += 1) {
        const a = punkte[i];
        const b = punkte[i + 1];
        if (local >= a.at && local <= b.at) {
            const spanne = b.at - a.at;
            if (spanne <= 0)
                return b.gain;
            return a.gain + ((local - a.at) / spanne) * (b.gain - a.gain);
        }
    }
    return letzter.gain;
}
/** Für die Anzeige: sind überhaupt Punkte gesetzt? */
export function hatPegelverlauf(clip) {
    return (clip.gainKeyframes ?? []).length > 0;
}
const f = (n) => {
    const g = Number(n.toFixed(4));
    return Number.isInteger(g) ? String(g) : String(g);
};
/**
 * Derselbe Verlauf als ffmpeg-Ausdruck für den `volume`-Filter, in der
 * Variablen `t` des Clips. `null`, wenn es nichts zu rechnen gibt — dann setzt
 * der Aufrufer den festen Wert, was billiger ist und im Filtergraph lesbar
 * bleibt.
 */
export function gainExpression(clip, tVar = 't') {
    const punkte = sortiert(clip);
    if (punkte.length === 0)
        return null;
    if (punkte.length === 1)
        return f(punkte[0].gain);
    // Von hinten nach vorne geschachtelt, wie bei den Bild-Keyframes: vor dem
    // ersten Punkt sein Wert, danach je Abschnitt eine Gerade.
    let ausdruck = f(punkte[punkte.length - 1].gain);
    for (let i = punkte.length - 2; i >= 0; i -= 1) {
        const a = punkte[i];
        const b = punkte[i + 1];
        const spanne = b.at - a.at;
        const anteil = spanne > 0 ? `clip((${tVar}-${f(a.at)})/${f(spanne)},0,1)` : '1';
        const gerade = `(${f(a.gain)}+(${f(b.gain - a.gain)})*${anteil})`;
        ausdruck = `if(lt(${tVar},${f(b.at)}),${gerade},${ausdruck})`;
    }
    const erster = punkte[0];
    return `if(lt(${tVar},${f(erster.at)}),${f(erster.gain)},${ausdruck})`;
}
/**
 * Der höchste Pegel, den die Linie auf dem Clip zeigt. Das Schema erlaubt bis
 * 4; gezeichnet wird bis 2, und was man mit dem Finger nicht sehen kann, soll
 * man mit ihm auch nicht setzen. Lauter geht weiter über das Blatt „Ton“.
 */
export const PEGEL_MAX = 2;
/**
 * Ein Pegelpunkt, den jemand auf dem Clip zieht: waagerecht die Zeit,
 * senkrecht der Pegel.
 *
 * Zwei Dinge hält die Funktion fest. Erstens bleibt ein Punkt zwischen seinen
 * Nachbarn -- zöge er über einen hinweg, stünde die Blende plötzlich verkehrt
 * herum, ohne dass jemand das gewollt hätte. Zweitens bleibt er im Clip: eine
 * Blende hinter dem Ende ist eine, die nie läuft.
 *
 * `griff` ist die Stelle, an der der Punkt lag, als der Finger aufkam -- nicht
 * seine neue: die Liste wird bei jeder Bewegung frisch aus dem Original
 * gerechnet, damit ein Zug hin und wieder zurück genau dort endet, wo er
 * begann.
 */
export function zieheGainKeyframe(keyframes, griff, ziel, duration, abstand = 0.02) {
    const punkte = [...(keyframes ?? [])].sort((a, b) => a.at - b.at);
    const i = punkte.findIndex((k) => k.at === griff);
    if (i < 0)
        return punkte;
    const links = i > 0 ? punkte[i - 1].at + abstand : 0;
    const rechts = i < punkte.length - 1 ? punkte[i + 1].at - abstand : Math.max(0, duration);
    const at = Math.min(Math.max(ziel.at, links), Math.max(links, rechts));
    // Auf ein Prozent genau: der Finger löst feiner auf, als ein Ohr hört, und
    // eine Zahl mit zwölf Nachkommastellen liest niemand gern im Blatt wieder.
    const gain = Math.min(PEGEL_MAX, Math.max(0, Math.round(ziel.gain * 100) / 100));
    return punkte.map((k, n) => (n === i ? { at, gain } : k));
}
//# sourceMappingURL=pegel.js.map